/**
 * Super Admin — Gupshup Partner (solo lectura).
 * Consumo, descuento, saldo y calificación por app.
 * El dueño no es técnico: cada columna dice si el dato vino de Gupshup o no.
 */
import { useCallback, useEffect, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import SectionHelp from '@/components/help/SectionHelp';
import { RefreshCw, Save, Loader2, Wallet } from 'lucide-react';

export default function GupshupPartner() {
  const [status, setStatus] = useState(null);
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [appWebhook, setAppWebhook] = useState('');
  const [apuntando, setApuntando] = useState(false);
  const [wabas, setWabas] = useState(null);
  const [borrando, setBorrando] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        api.get('/admin/gupshup-partner/status'),
        api.get('/admin/gupshup-partner/dashboard?dias=14'),
      ]);
      setStatus(s.data);
      setDash(d.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo cargar Gupshup Partner');
    } finally {
      setLoading(false);
    }
  }, []);

  const cargarWabas = useCallback(async () => {
    try {
      const r = await api.get('/admin/gupshup-partner/apps');
      setWabas(r.data?.items || []);
    } catch (e) {
      setWabas([]); // sin credenciales o error: la sección muestra vacío, no rompe
    }
  }, []);

  const borrarWaba = async (appId, appName) => {
    if (!appId) return;
    const nombre = appName || appId;
    if (!window.confirm(
      `¿Eliminar DEFINITIVAMENTE la WABA «${nombre}» de tu cuenta de Gupshup?\n\n` +
      `Esto la borra en Gupshup por API y NO se puede deshacer. Si un negocio activo la ` +
      `está usando, el sistema no te dejará borrarla.`)) return;
    setBorrando(appId);
    try {
      await api.delete(`/admin/gupshup-partner/apps/${encodeURIComponent(appId)}`,
                       { params: { confirm_app_id: appId } });
      toast.success(`WABA «${nombre}» eliminada de Gupshup`);
      await cargarWabas();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo eliminar la WABA');
    } finally {
      setBorrando(null);
    }
  };

  useEffect(() => { load(); cargarWabas(); }, [load, cargarWabas]);

  const guardarCreds = async () => {
    if (!email.trim() || !password) {
      toast.error('Escribe el correo y la contraseña (o secret) del portal Partner');
      return;
    }
    setSaving(true);
    try {
      await api.put('/admin/gupshup-partner/credentials', {
        email: email.trim(),
        password,
        display_name: 'Gupshup Partner (Tecprovider)',
      });
      toast.success('Credenciales guardadas. No se guardan en el chat ni en archivos de código.');
      setPassword('');
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudieron guardar');
    } finally {
      setSaving(false);
    }
  };

  const syncAhora = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/admin/gupshup-partner/sync');
      // Se dice el RANGO, no solo el día final: ahora reprocesa una ventana
      // porque Gupshup publica con retraso, y decir «sincronizado el 08-07»
      // haría pensar que solo se miró ese día.
      const rango = r.data.desde ? `${r.data.desde} → ${r.data.fecha}` : r.data.fecha;
      toast.success(
        `Sincronizado ${rango}: ${r.data.filas_upsert || 0} fila(s), ${r.data.apps_vistas || 0} app(s)`,
      );
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'Sync falló');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-sm text-[#64748B]">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando…
      </div>
    );
  }

  const apps = dash?.apps || [];
  const uso = dash?.uso_reciente || [];
  const alertas = dash?.alertas_reputacion || [];
  const porNegocio = dash?.resumen_por_negocio || [];

  // Un costo se enseña con seis decimales o no se enseña: el mensaje cuesta
  // milésimas de dólar, y redondear a centavos lo convierte en «$0.00».
  const usd = (v, decimales = 6) =>
    v === null || v === undefined ? 'no disponible' : `$${Number(v).toFixed(decimales)}`;

  const acusarAlerta = async (id) => {
    try {
      await api.post(`/admin/gupshup-partner/alerts/${id}/acknowledge`);
      toast.success('Aviso marcado como visto');
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo marcar');
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-6xl" data-testid="admin-gupshup-partner">
      <div>
        <h1 className="text-xl font-bold text-[#0F172A] flex items-center gap-2">
          <Wallet className="w-5 h-5" /> Gupshup Partner
        </h1>
        <p className="text-sm text-[#64748B] mt-1">
          Lectura de tu cuenta de partner (Tecprovider): apps, consumo, descuento y saldo.
          <strong> No envía mensajes ni crea plantillas.</strong>
        </p>
      </div>

      <SectionHelp
        module="admin_gupshup_partner"
        title="Qué es esto"
        description="CavernicolApp es partner de Gupshup. Aquí ves lo que la Partner API reporta por cada app WhatsApp bajo tu cuenta. El descuento es el que Gupshup te devuelve — no se inventa un margen."
        whatItDoesNot="No crea apps en Gupshup, no manda WhatsApp, no aprueba plantillas de Meta ni calcula un porcentaje de ganancia si la API no lo manda."
      />

      {/* Credenciales */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-bold text-[#0F172A]">Credenciales del portal Partner</h2>
        <p className="text-xs text-[#64748B]">
          {status?.configurado
            ? `Configurado (${status.email_enmascarado || 'email guardado'}). Puedes reemplazar la contraseña abajo.`
            : 'Aún no hay credenciales. Entra al portal Partner de Gupshup y usa el mismo correo/secret aquí.'}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-w-xl">
          <div>
            <label className="text-xs text-[#64748B]">Correo Partner</label>
            <Input className="h-9 text-sm" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@correo.com" data-testid="gupshup-partner-email" />
          </div>
          <div>
            <label className="text-xs text-[#64748B]">Contraseña o secret</label>
            <Input className="h-9 text-sm" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="gupshup-partner-password" />
          </div>
        </div>
        <Button size="sm" onClick={guardarCreds} disabled={saving} data-testid="gupshup-partner-save-creds">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Save className="w-3.5 h-3.5 mr-1" />}
          Guardar credenciales
        </Button>
      </div>


      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3" data-testid="gupshup-alta-asistida">
          <h2 className="text-sm font-bold text-[#0F172A]">Webhook de CavernicolApp</h2>
          <p className="text-xs text-[#64748B]">
            Esta es la URL que Gupshup debe llamar. En el portal nuevo (gupshup.ai)
            casi no hay campo para pegarla: o la pones en Settings/Callback de la app,
            o la registramos aquí con el App ID.
          </p>
          <code className="block text-xs bg-[#F1F5F9] border rounded px-2 py-2 break-all">
            https://app.cavernicolapp.com/api/webhooks/gupshup
          </code>
          {!status?.configurado && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
              Para registrarla sin entrar a Gupshup, primero guarda arriba el correo
              y el secret del portal Partner.
            </p>
          )}
          <div className="flex flex-wrap gap-2 items-end">
            <div>
              <label className="text-xs text-[#64748B]">ID de la app que ya existe</label>
              <Input className="h-9 text-sm w-80" value={appWebhook} onChange={(e) => setAppWebhook(e.target.value)} placeholder="uuid de la app en Gupshup" />
            </div>
            <Button size="sm" disabled={apuntando || !appWebhook.trim() || !status?.configurado} onClick={async () => {
              setApuntando(true);
              try {
                const r = await api.post('/admin/gupshup-partner/apuntar-webhook/' + appWebhook.trim());
                toast.success(r.data.aviso);
              } catch (e) {
                toast.error(formatApiError(e.response?.data?.detail) || 'No se pudo registrar el webhook');
              } finally { setApuntando(false); }
            }}>{apuntando ? 'Registrando…' : 'Registrar webhook'}</Button>
          </div>
        </div>

      {/* Alertas de reputación — solo avisar */}
      {alertas.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 space-y-3" data-testid="gupshup-partner-alerts">
          <h2 className="text-sm font-bold text-amber-950">Avisos de calificación</h2>
          <p className="text-xs text-amber-900">
            La calificación de calidad de un número bajó. <strong>No se suspendió nada automáticamente</strong> —
            revisa en Meta/Gupshup y decide a mano. El cortacircuitos solo detecta y avisa.
          </p>
          <ul className="space-y-2">
            {alertas.map((a) => (
              <li key={a.id} className="bg-white border border-amber-100 rounded-lg p-3 text-xs text-[#0F172A]">
                <p className="font-medium">{a.app_name || a.gupshup_app_id}</p>
                <p className="text-[#64748B] mt-0.5">{a.message}</p>
                <p className="text-[10px] text-[#94A3B8] mt-1">
                  {a.previous_label} → {a.new_label}
                  {a.detected_at ? ` · ${new Date(a.detected_at).toLocaleString('es-MX')}` : ''}
                </p>
                <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={() => acusarAlerta(a.id)}>
                  Marcar como visto
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Mis WABAs en Gupshup — eliminar vía API */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3"
           data-testid="gupshup-wabas-panel">
        <h2 className="text-sm font-bold text-[#0F172A]">Tus WABAs en Gupshup</h2>
        <SectionHelp
          module="admin_gupshup_wabas"
          title="Qué es esto"
          description="La lista de las cuentas de WhatsApp (WABAs / apps) que tienes en tu cuenta de Gupshup. Desde aquí puedes eliminar por API una que ya no uses."
          whatItDoesNot="Eliminar una WABA la borra en Gupshup y NO se puede deshacer. Por seguridad, el sistema no te deja borrar una que un negocio ACTIVO esté usando: primero desconéctala de ese negocio o elimina el negocio. Solo un usuario Dios (dueño/super admin) puede eliminar."
        />
        {wabas === null ? (
          <p className="text-xs text-[#94A3B8]">Cargando…</p>
        ) : wabas.length === 0 ? (
          <p className="text-xs text-[#94A3B8]">No hay WABAs para mostrar (o faltan las credenciales del portal Partner arriba).</p>
        ) : (
          <ul className="space-y-2">
            {wabas.map((w) => (
              <li key={w.gupshup_app_id || w.app_name}
                  className="flex items-center justify-between gap-3 bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-[#0F172A] truncate">{w.app_name || '(sin nombre)'}</p>
                  <p className="text-[11px] text-[#94A3B8] truncate">{w.gupshup_app_id}</p>
                </div>
                <Button size="sm" variant="outline"
                        className="h-8 text-xs text-rose-700 border-rose-200 hover:bg-rose-50 whitespace-nowrap"
                        disabled={!w.gupshup_app_id || borrando === w.gupshup_app_id}
                        onClick={() => borrarWaba(w.gupshup_app_id, w.app_name)}>
                  {borrando === w.gupshup_app_id ? 'Eliminando…' : 'Eliminar'}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Cuánto cuesta WhatsApp en cada producto tuyo */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3"
           data-testid="gupshup-costo-por-negocio">
        <h2 className="text-sm font-bold text-[#0F172A]">Costo por negocio</h2>
        <SectionHelp
          module="admin_gupshup_costo_por_negocio"
          title="Para qué sirve esta tabla"
          description="Tienes dos productos colgando de la misma cuenta de Gupshup, así que el gasto total no sirve para ponerle precio a ninguno. Aquí cada uno va por separado, y la última columna es lo que te cuesta a ti un mensaje: el piso sobre el que pones el precio del bloque que le vendes a un socio."
          whatItDoesNot="No es lo que cobras: es lo que pagas. Y solo cuenta lo que Gupshup ya facturó, así que los días más recientes pueden entrar con retraso."
        />
        {porNegocio.length === 0 ? (
          <p className="text-sm text-[#64748B]">
            Todavía no hay consumo guardado. Se llena solo cada noche, o puedes
            adelantarlo con el botón «Sincronizar» de abajo.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="gupshup-negocio-table">
              <thead>
                <tr className="text-left text-[#64748B] border-b border-[#E2E8F0]">
                  <th className="py-2 pr-3 font-medium">Negocio</th>
                  <th className="py-2 pr-3 font-medium">Apps</th>
                  <th className="py-2 pr-3 font-medium">Mensajes</th>
                  <th className="py-2 pr-3 font-medium">Entrantes</th>
                  <th className="py-2 pr-3 font-medium">Salientes</th>
                  <th className="py-2 pr-3 font-medium">Costo</th>
                  <th className="py-2 pr-3 font-medium">Te cuesta el mensaje</th>
                </tr>
              </thead>
              <tbody>
                {porNegocio.map((n) => (
                  <tr key={n.negocio} className="border-b border-[#F1F5F9]">
                    <td className="py-2 pr-3 font-medium text-[#0F172A]">{n.negocio}</td>
                    <td className="py-2 pr-3 text-[#64748B]">{n.apps}</td>
                    <td className="py-2 pr-3 text-[#64748B]">{n.mensajes}</td>
                    <td className="py-2 pr-3 text-[#64748B]">{n.entrantes}</td>
                    <td className="py-2 pr-3 text-[#64748B]">{n.salientes}</td>
                    <td className="py-2 pr-3 text-[#0F172A]">{usd(n.costo_usd, 4)}</td>
                    <td className="py-2 pr-3 font-medium text-[#0F172A]">
                      {n.costo_por_mensaje === null || n.costo_por_mensaje === undefined
                        ? <span className="text-[#94A3B8]">sin mensajes todavía</span>
                        : usd(n.costo_por_mensaje)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-[10px] text-[#94A3B8] mt-2">
              Últimos {porNegocio[0]?.dias || 14} días. «Sin clasificar» son apps
              cuyo negocio nadie ha dicho todavía.
            </p>
          </div>
        )}
      </div>

      {/* Apps en vivo */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-sm font-bold text-[#0F172A]">Apps en Gupshup</h2>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={load} data-testid="gupshup-partner-refresh">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Actualizar
            </Button>
            <Button size="sm" onClick={syncAhora} disabled={syncing || !status?.configurado} data-testid="gupshup-partner-sync">
              {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
              Sincronizar
            </Button>
          </div>
        </div>
        {!dash?.configurado && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3">
            {dash?.mensaje || 'Gupshup Partner no está configurado.'}
          </p>
        )}
        {dash?.configurado && apps.length === 0 && (
          <p className="text-sm text-[#64748B]">
            {dash.mensaje || 'No hay apps listadas (o la API no devolvió ninguna).'}
          </p>
        )}
        {apps.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="gupshup-partner-apps-table">
              <thead>
                <tr className="text-left text-[#64748B] border-b border-[#E2E8F0]">
                  <th className="py-2 pr-3 font-medium">App</th>
                  <th className="py-2 pr-3 font-medium">ID</th>
                  <th className="py-2 pr-3 font-medium">Salud</th>
                  <th className="py-2 pr-3 font-medium">Calificación</th>
                  <th className="py-2 pr-3 font-medium">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {apps.map((a) => (
                  <tr key={a.gupshup_app_id} className="border-b border-[#F1F5F9]">
                    <td className="py-2 pr-3 font-medium text-[#0F172A]">{a.app_name}</td>
                    <td className="py-2 pr-3 font-mono text-[10px] text-[#64748B]">{a.gupshup_app_id}</td>
                    <td className="py-2 pr-3">{String(a.salud)}</td>
                    <td className="py-2 pr-3">{String(a.calificacion)}</td>
                    <td className="py-2 pr-3">{a.saldo_label || 'no disponible'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {dash?.nota && <p className="text-[11px] text-[#94A3B8]">{dash.nota}</p>}
      </div>

      {/* Uso reciente DB */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-bold text-[#0F172A]">Consumo reciente (sincronizado)</h2>
        <p className="text-xs text-[#64748B]">
          Filas guardadas cada noche por el cron, o por el botón «Sincronizar».
          Se revisan los últimos 7 días en cada pasada, porque Gupshup publica
          con retraso y un día puede llegar dos días tarde. Si un campo no vino
          de Gupshup, dice «no disponible».
        </p>
        {uso.length === 0 ? (
          <p className="text-sm text-[#94A3B8]">Aún no hay filas. Configura credenciales y sincroniza.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="gupshup-partner-usage-table">
              <thead>
                <tr className="text-left text-[#64748B] border-b border-[#E2E8F0]">
                  <th className="py-2 pr-2 font-medium">Fecha</th>
                  <th className="py-2 pr-2 font-medium">App</th>
                  <th className="py-2 pr-2 font-medium">Negocio</th>
                  <th className="py-2 pr-2 font-medium">Costo</th>
                  <th className="py-2 pr-2 font-medium">Descuento</th>
                  <th className="py-2 pr-2 font-medium">Saldo cierre</th>
                  <th className="py-2 pr-2 font-medium">Categorías</th>
                </tr>
              </thead>
              <tbody>
                {uso.map((u) => (
                  <tr key={u.id} className="border-b border-[#F1F5F9]">
                    <td className="py-2 pr-2 whitespace-nowrap">{u.fecha}</td>
                    <td className="py-2 pr-2">{u.app_name || u.gupshup_app_id}</td>
                    <td className="py-2 pr-2">
                      {u.negocio || <span className="text-[#94A3B8]">sin clasificar</span>}
                    </td>
                    <td className="py-2 pr-2">{u.costo_label}</td>
                    <td className="py-2 pr-2">{u.descuento_label}</td>
                    <td className="py-2 pr-2">
                      {u.saldo_al_cierre_usd != null ? u.saldo_al_cierre_usd : 'no disponible'}
                    </td>
                    <td className="py-2 pr-2 text-[10px] text-[#64748B] max-w-[220px] truncate">
                      {u.conversaciones_por_categoria && Object.keys(u.conversaciones_por_categoria).length
                        ? Object.entries(u.conversaciones_por_categoria)
                          .map(([k, v]) => `${k}:${v}`)
                          .join(' · ')
                        : 'no disponible'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
